
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import moment from "moment";
import { cloneDeep } from "lodash";

import GET_PROJECTS from "@/apollo/scheduler/GetProjects.gql"

import SchedulerOverlay from '../overlays/SchedulerOverlay.vue';
import Scheduler from '../Scheduler.vue';
import FormGroup from "@/components/form/FormGroup.vue";
import ProjectViewActivityItem from "@/components/scheduler/itemComponents/ProjectViewActivityItem.vue";
import ProjectViewProjectItem from "@/components/scheduler/itemComponents/ProjectViewProjectItem.vue";
import ProjectGroup from "@/components/scheduler/groupComponents/ProjectGroup.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import ProjectsMultiselect from "@/components/autocompleteMultiselects/ProjectsMultiselect.vue";
import ProjectStatusSelect from "@/components/scheduler/schedulerSelects/ProjectStatusSelect.vue";
import ProjectModal from "@/components/companyProjects/ProjectModal.vue";
import ActivityFormModal from "@/components/projects/activities/ActivityFormModal.vue";
import ProjectViewAdditionalFilters from "@/components/scheduler/additionalFilters/ProjectViewAdditionalFIlters.vue";
import UiButton from "@/components/button/ui-button.vue";
import SchedulerCustomModalHeader from "@/components/scheduler/SchedulerCustomModalHeader.vue";
import AddionalFilterCard from '../scheduler/AdditionalFilterCard.vue';

import Message from '@/data/notificationsMessages';
import projectViewFiltersTemplate from "@/data/filtersTemplates/scheduler/projectViewFiltersTemplate";
import { defaultErrorNotificationMessage } from "@/data/errorMessages";
import { Project, Project_Activity_Bool_Exp, Project_Bool_Exp, User } from "@/generated/graphql";
import { NOTIFICATION } from "@/entities/Events";
import SchedulerItemEntities from "@/entities/scheduler/SchedulerItemEntities";
import ProjectCriteriaOptions from "@/entities/scheduler/ProjectCriteriaOptions";
import ProjectVIewFiltersToWatch from "@/entities/scheduler/additionalFiltersInterfaces/ProjectViewFiltersToWatch";
import RangeChangedPayload from "@/entities/scheduler/RangeChangedPayload";
import ProjectViewManager from "@/entities/scheduler/ProjectViewManager";
import ProjectViewActivity from "@/entities/scheduler/ProjectViewActivity";
import getFetchMoreStartDates from "@/helpers/scheduler/getFetchMoreStartDates";
import CustomTimelineItem from "@/entities/scheduler/CustomTimelineItem";
import CustomTimelineGroup from "@/entities/scheduler/CustomTimelineGroup";
import TimelineOptionsWithTemplates from "@/entities/scheduler/TimelineOptionsWithTemplates";
import { getEmptyFiltersResultMessage, getEmptyListMessage } from "@/helpers/lists/emptyListMessages";
import getFullName from "@/helpers/getFullName";
import SelectPayload from "@/entities/scheduler/SelectPayload";
import RouteNames from "@/entities/RouteNames";
import RouteParams from "@/entities/RouteParams";
import ActivityDataForModal from "@/entities/modals/ActivityDataForModal";
import NewlyCreatedSchedulerItem from "@/entities/scheduler/NewlyCreatedSchedulerItem";
import Roles from "@/entities/Roles";
import ProjectDataForModal from "@/entities/modals/ProjectDataForModal";
import ProjectAdvancedFilter from '../scheduler/filters/ProjectAdvancedFilter.vue';

@Component({
    components: {
        SchedulerOverlay,
        Scheduler,
        ProjectViewActivityItem,
        ProjectViewProjectItem,
        ProjectGroup,
        LoadingOverlay,
        FormGroup,
        ProjectsMultiselect,
        ProjectStatusSelect,
        ProjectModal,
        ActivityFormModal,
        ProjectViewAdditionalFilters,
        UiButton,
        SchedulerCustomModalHeader,
        AddionalFilterCard,
        ProjectAdvancedFilter
    },
    apollo: {
        projects: {
            query: GET_PROJECTS,
            update: data => data.project,
        }
    }
})
export default class ProjectScheduler extends Vue {
    @Prop({ type: Object, required: true }) readonly currentUser!: User;
    @Prop({ type: String, required: true }) readonly companyId!: string;
    @Prop({ type: Date, default: undefined }) readonly axisStart?: Date;
    @Prop({ type: Date, default: undefined }) readonly axisEnd?: Date;
    @Prop({ type: Array, default: () => [] }) readonly selectedProjectsIds!: string[];
    @Prop({ type: Boolean, default: false }) readonly showAdditionalFilters!: boolean;
    @Prop({ type: Boolean, default: false }) readonly shouldClearFilters!: boolean;
    @Prop({ type: Boolean, default: false }) showActivities!: boolean;

    schedulerItemEntities = SchedulerItemEntities;
    queryStartTime: Date = moment().startOf('month').subtract(2, 'w').toDate();
    queryEndTime: Date = moment().endOf('month').toDate();
    isInitialProjectsFetchingDone: boolean = false;
    numberOfAppliedFilters = 0;

    projects: Project[] = [];
    passedStartDate: Date = moment().startOf('month').toDate();
    passedEndDate: Date = moment().endOf('month').toDate();

    activityInfoForModal?: ActivityDataForModal | null = null;
    projectInfoForModal?: Project | null = null;

    isSelectedItemManager: boolean = false;
    modalShown: boolean = false;
    newlyCreatedItemId?: string = undefined;

    isEdittingLocked: boolean = true;

    additionalFilters: ProjectVIewFiltersToWatch = cloneDeep(projectViewFiltersTemplate);

    created(): void {
        this.$apollo.queries.projects.setVariables({
            where: this.where,
            activitiesWhere: this.activitiesWhere,
        });
        this.$apollo.queries.projects.skip = false;

        this.$root.$on("projectsChanged", () => {
            this.$apollo.queries.projects.refetch();
        })
    }
    message: string = 'You haven\'t added any projects yet';


    get type() {
        return 'Project';
    }

    projectColors: string[] = [
        "#1A7BEE",
        "#8B3EFF",
        "#FF8E3C",
        "#01B549",
        "#DA1B8F"
    ]

    getRandColor() {
        return this.projectColors[Math.floor(Math.random() * this.projectColors.length)];
    }

    @Watch("propToWatch")
    refetchOnDataFiltersChange(oldVal: string[], newVal: string[]) {
        if (oldVal !== newVal) {
            this.$apollo.queries.projects.refetch({
                where: this.where,
                activitiesWhere: this.activitiesWhere,
            });
        }
    }

    @Watch("numberOfAppliedFilters", { immediate: true })
    appliedFiltersChanged() {
        this.$emit("appliedFiltersChanged", this.numberOfAppliedFilters);
    }

    get loading(): boolean {
        return this.$apollo.queries.projects.loading && !this.projects.length
    }

    get shouldBlockEditting(): boolean {
        return this.isEdittingLocked && !Boolean(this.activityInfoForModal?.isNew);
    }

    get linkToProjectActivities(): RouteParams<{ id: string }> {
        return {
            name: RouteNames.PROJECT_ACTIVITY,
            params: { id: this.projectInfoForModal?.id || "" }
        }
    }

    get customPlaceholderMessage(): string {
        if (this.numberOfAppliedFilters && !this.projects.length) {
            return getEmptyFiltersResultMessage("projects");
        }
        return getEmptyListMessage("projects");
    }


    @Watch("projectIds")
    updateSelectedIds(): void {
        if (this.isInitialProjectsFetchingDone) {
            return;
        }
        this.$emit('selectedProjectIdsUpdated', this.projectIds);
        this.isInitialProjectsFetchingDone = true;
    }

    get propToWatch(): string {
        return JSON.stringify([
            this.selectedProjectsIds,
            this.additionalFilters,
        ]);
    }

    get linkToProject(): RouteParams<{ id: string }> {
        return {
            name: RouteNames.PROJECT_ACTIVITY,
            params: { id: this.projectInfoForModal?.id || "" }
        }
    }

    get showActivityModal(): boolean {
        return this.modalShown && !this.isSelectedItemManager;
    }

    get showProjectModal(): boolean {
        return this.modalShown && this.isSelectedItemManager;
    }

    get isManager(): boolean {
        return this.currentUser.role === Roles.MANAGER;
    }

    get projectIds(): string[] {
        return this.projects.map(project => project.id);
    }

    get projectGroups(): CustomTimelineGroup[] {
        if (!this.projects) {
            return [];
        }
        return this.projects.map(project => {
            return {
                id: project.id,
                name: project.name!,
            }
        })
    }

    get projectItems(): (ProjectViewManager | ProjectViewActivity)[] {
        if (!this.projects) {
            return [];
        }
        return this.projects
            .map((project) => {
                const groupId = project.id;
                let manager: ProjectViewManager = {
                    id: `${project.id}`,
                    group: groupId,
                    start: project.start_date,
                    end: project.end_date,
                    projectActivities: project.project_activities,
                    className: "manager-bg",
                    entity: SchedulerItemEntities.MANAGER,
                    entityInfo: {
                        projectName: project.name || "",
                        project,
                    }
                };
                if (project.manager) {
                    manager = {
                        ...manager,
                        id: `${project.id}${project.manager.id}`,
                        content: project.manager!.user.given_name!,
                        entityInfo: {
                            ...manager.entityInfo,
                            id: project.manager!.user.id || "",
                            avatar: project.manager.user.avatar || "",
                            managerName: getFullName(project.manager!.user.given_name!, project.manager!.user.family_name!),
                            requests: project.manager!.contractors,
                        }
                    }
                }

                const activities: ProjectViewActivity[] = project.project_activities
                    .map(activity => {
                        const activityStart = Boolean(activity?.start_date)
                            ? activity?.start_date
                            : project.start_date;
                        let activityEnd = Boolean(activity?.end_date)
                            ? activity?.end_date
                            : moment(project.start_date).add(1, 'd').toDate();
                        if (activityStart === activityEnd) {
                            activityEnd = moment(activity.start_date).add(24, 'hours').toDate();
                        }
                        return {
                            id: activity.id,
                            group: groupId,
                            content: activity.title,
                            className: "smallItem activityColor",
                            start: activityStart,
                            end: activityEnd,
                            entityInfo: {
                                contractor: activity.contractor,
                                activity: activity as ActivityDataForModal,
                                project,
                                name: activity.title,
                            }
                        }
                    });

                if (this.showActivities && manager) {
                    return [...activities, manager];
                }
                if (manager) {
                    return [manager];
                }
                if (this.showActivities) {
                    return activities;
                }
                if (this.showActivities) {
                    return activities;
                }
                return [];
            })
            .flat();
    }

    get projectsFormated() {
        return this.projects.map(project => {
            return {
                id: project?.id || "",
                content: project?.name! || "",
                start: project?.start_date || "",
                end: project?.end_date || "",
                color: this.getRandColor(),
                completed: project?.completed || false,
                project: project as ProjectDataForModal || {},
            }
        })
    }

    get options(): TimelineOptionsWithTemplates {
        return {
            start: this.passedStartDate,
            end: this.passedEndDate,
            autoResize: true,
            stack: true,
            onAdd: function (item, callback) {
                item.className = "activityColor smallItem newItem";
                callback(item);
            },
            order: function (currentItem: CustomTimelineItem, nextItem: CustomTimelineItem): number {
                const currentItemStart = typeof currentItem.start === "string"
                    ? new Date(currentItem.start)
                    : currentItem.start;
                const nextItemStart = typeof nextItem.start === "string"
                    ? new Date(nextItem.start)
                    : nextItem.start;
                return +nextItemStart - +currentItemStart;
            },
        }
    }

    get activitiesIds(): string[] {
        return this.projects
            .map(project => {
                return project.project_activities
                    .map(activity => activity.id)
            })
            .flat();
    }

    get activitiesWhere(): Project_Activity_Bool_Exp {
        return {
            _or: [
                {
                    start_date: {
                        _lte: this.queryEndTime,
                        _gte: this.queryStartTime,
                    }
                },
                {
                    end_date: {
                        _lte: this.queryEndTime,
                        _gte: this.queryStartTime,
                    }
                },
                {
                    _and: [
                        {
                            start_date: {
                                _lte: this.queryStartTime,
                            }
                        },
                        {
                            end_date: {
                                _gte: this.queryEndTime
                            }
                        }
                    ]
                }
            ]
        }
    }

    get where(): Project_Bool_Exp {
        const filterConditions = [];
        const {
            countries,
            states,
            cities,
            status,
            criteria,
        } = this.additionalFilters;

        const statusIds = status.map(status => status.id);
        const criteriaNames = criteria.map(criteria => criteria.name);

        if (!this.selectedProjectsIds.length && !this.isInitialProjectsFetchingDone) {
            filterConditions.push({
                end_date: {
                    _gte: this.queryStartTime,
                }
            });
        }

        if (this.selectedProjectsIds.length) {
            filterConditions.push({
                id: {
                    _in: this.selectedProjectsIds,
                },
            });
        }

        if (countries.length) {
            filterConditions.push({
                country: { _in: countries },
            });
        }

        if (states.length) {
            filterConditions.push({
                state: { _in: states },
            });
        }

        if (cities.length) {
            filterConditions.push({
                city: { _in: cities },
            });
        }

        if (criteriaNames.length) {
            if (criteriaNames.includes(ProjectCriteriaOptions.PROJECTS_NEEDS_CONTRACTORS)) {
                filterConditions.push({
                    project_activities: {
                        contractor_id: {
                            _is_null: true,
                        }
                    }
                });
            }

            if (criteriaNames.includes(ProjectCriteriaOptions.PROJECTS_WITHOUT_MANAGER)) {
                filterConditions.push({
                    manager_id: {
                        _is_null: true,
                    }
                });
            }

            if (criteriaNames.includes(ProjectCriteriaOptions.PROJECTS_WITHOUT_ACTIVITIES)) {
                filterConditions.push({
                    _not: { project_activities: {} }
                });
            }
        }

        if (statusIds.length && statusIds[0] !== "All") {
            filterConditions.push({
                _and: [
                    { completed: { _eq: statusIds[0] !== "Active" } },
                ],
            })
        }
        return { _and: filterConditions };
    }

    async rangeChanged(e: RangeChangedPayload): Promise<void> {
        this.$emit('rangechanged', e);
        const fetchMoreDates = getFetchMoreStartDates(this.queryStartTime, this.queryEndTime, e);
        if (!fetchMoreDates) {
            return;
        }
        const { queryStartTime, queryEndTime, whereDatesConditions } = fetchMoreDates;
        this.queryStartTime = queryStartTime;
        this.queryEndTime = queryEndTime;
        try {
            await this.$apollo.queries.projects.fetchMore({
                variables: {
                    where: this.where,
                    activitiesWhere: { _or: whereDatesConditions },
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                    const updatedProjects: Project[] = fetchMoreResult.project;
                    const previousUpdatedProjects = previousResult.project.map((project: Project) => {
                        const correspondingProject: Project | undefined = updatedProjects.find(updatedProject => {
                            return updatedProject.id === project.id;
                        });
                        if (correspondingProject) {
                            const newActivities = correspondingProject.project_activities.filter(activity => {
                                return !this.activitiesIds.includes(activity.id);
                            });
                            return {
                                ...project,
                                project_activities: [...project.project_activities, ...newActivities],
                                __typename: "project"
                            }
                        } else {
                            return project;
                        }
                    });
                    const newlyAddedProjects = updatedProjects
                        .filter((project) => !previousUpdatedProjects
                            .map((project: Project) => project.id)
                            .includes(project.id));

                    return {
                        project: [...previousUpdatedProjects, ...newlyAddedProjects]
                    }
                }
            })
        } catch (e) {
            this.$eventBus.emit(
                NOTIFICATION,
                Message.errorMessage(defaultErrorNotificationMessage),
            );
        }
    }

    handleIconClose(): void {
        this.$emit('close');
        this.$apollo.queries.projects.refetch();
    }
    handleSelect(payload: SelectPayload): void {
        const selectedItemId: string = payload?.items?.[0];
        if (!selectedItemId) {
            return;
        };

        const selectedItem: ProjectViewManager | ProjectViewActivity = this.projectItems
            .filter(item => item.id === selectedItemId)[0];
        if (!selectedItem) {
            this.newlyCreatedItemId = selectedItemId;
            this.isSelectedItemManager = false;
            return;
        }
        this.isSelectedItemManager = selectedItem.entity === SchedulerItemEntities.MANAGER;

        if (this.isSelectedItemManager) {
            this.projectInfoForModal = (selectedItem as ProjectViewManager)!.entityInfo!.project;
        } else {
            this.projectInfoForModal = (selectedItem as ProjectViewActivity).entityInfo.project;
            this.activityInfoForModal = (selectedItem as ProjectViewActivity).entityInfo.activity;
        }
        this.modalShown = true;
    }

    handleCreateBtnClicked(activity: NewlyCreatedSchedulerItem) {
        this.activityInfoForModal = {
            title: "",
            start_date: activity.start,
            end_date: "",
            note: "",
            budget: "",
            confirmed_hours: 0,
            requested_hours: 0,
            isNew: true,
        };
        this.setCurrentlyFetchedItems();
        this.projectInfoForModal = this.projects.filter(project => project.id === activity.group)[0];
        this.modalShown = true;
    }

    handleCloseModal(): void {
        if (this.modalShown == false) {
            return;
        }
        this.clearSelectedItems();
        if (this.newlyCreatedItemId) {
            this.clearNewlyCreatedItemId();
            this.setCurrentlyFetchedItems();
        }
        this.closeModal();
    }

    async handleCollectionUpdated(): Promise<void> {
        this.clearNewlyCreatedItemId();
        this.clearSelectedItems();
        await this.refetchCollection();
        this.closeModal();
    }

    clearSelectedItems(): void {
        const timeline = this.$refs.timeline as any;
        timeline.setSelection([]);
    }

    clearNewlyCreatedItemId(): void {
        this.newlyCreatedItemId = undefined;
    }

    closeModal(): void {
        this.modalShown = false;
        this.isEdittingLocked = true;
    }

    setCurrentlyFetchedItems(): void {
        const timeline = this.$refs.timeline as any;
        timeline.setItems(this.projectItems);
    }

    async refetchCollection(): Promise<void> {
        this.$apollo.queries.projects.refetch({
            where: this.where,
            activitiesWhere: this.activitiesWhere,
        });
    }

    handleAppliedFiltersNumberChanged(num: number): void {
        this.numberOfAppliedFilters = num;
    }

    handleFiltersChanged(filters: ProjectVIewFiltersToWatch): void {
        this.additionalFilters.countries = filters.countries;
        this.additionalFilters.states = filters.states;
        this.additionalFilters.cities = filters.cities;
        this.additionalFilters.criteria = filters.criteria;
        this.additionalFilters.status = filters.status;
    }

    handleShowActivitiesToggle(toggleState: boolean): void {
        this.showActivities = toggleState;
    }

    handleEditClick(): void {
        this.isEdittingLocked = false;
    }

    handleTimeRangeChanged(rangeStart: Date): void {
        this.passedStartDate = rangeStart
    }

    handleTimeRangeChangedEnd(rangeEnd: Date): void {
        this.passedEndDate = rangeEnd
    }
}
